import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SpinnerDotted } from "spinners-react";
import { clickSendSms } from "../components/SendSMS";
import axios from "axios";
import { deviceHeaders } from "../api/deviceTracking";

export const SendTextPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transferData, setTransferData] = useState(null);
  const [messageSent, setMessageSent] = useState(false);
  const [waitingForShortlink, setWaitingForShortlink] = useState(false);
  const [shortlinkTimeout, setShortlinkTimeout] = useState(false);
  
  const transferId = searchParams.get("transfer_id");
  const uniqueId = searchParams.get("unique_id");

  useEffect(() => {
    const fetchTransferDetails = async () => {
      if (!transferId || !uniqueId) {
        setError("Missing required parameters");
        setLoading(false);
        return;
      }

      try {
        // Use the external endpoint that doesn't require authentication
        const baseUrl = process.env.REACT_APP_API_URL.replace("/embedded", "");
        const response = await axios.get(
          `${baseUrl}/external/embedded/transfer/sendtext/${transferId}/${uniqueId}`,
          { headers: await deviceHeaders("sending-app") }
        );
        setTransferData(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching transfer details:", err);
        setError("Failed to fetch transfer details");
        setLoading(false);
      }
    };

    fetchTransferDetails();
  }, [transferId, uniqueId]);

  useEffect(() => {
    if (transferData && !messageSent) {
      // Extract transfer and branding from response
      const transfer = transferData;
      const branding = transfer.branding;
      
      // Check transfer status first
      if (transfer.status === "FAILED") {
        // Failed transfer - don't send any messages
        setWaitingForShortlink(false);
        return;
      }
      
      // Find the shortlink from events
      const shortlinkEvent = transfer.events?.find(
        event => event.event === "shortlink_generated"
      );
      const shortlink = shortlinkEvent?.url;
      
      // If no shortlink yet and this is a phone recipient, start waiting
      if (!shortlink && transfer.recipient?.type === "phone" && !waitingForShortlink && !shortlinkTimeout) {
        setWaitingForShortlink(true);
        
        // Start 20-second timeout
        const timeout = setTimeout(() => {
          setShortlinkTimeout(true);
          setWaitingForShortlink(false);
        }, 20000);
        
        return () => clearTimeout(timeout);
      }
      
      // Check if recipient is phone or email
      if (transfer.recipient?.type === "email") {
        setMessageSent(true);
        setWaitingForShortlink(false);
      } else if (transfer.recipient?.type === "phone" && transfer.recipient?.value) {
        // Construct SMS message based on transfer type
        let message = "";
        if (shortlink) {
          // Off-network transfer with shortlink
          message = `${branding?.sms_customization?.off_net_first || "You've received"} $${transfer.amount?.value} ${branding?.sms_customization?.off_net_second || "from"} ${transfer.financial_institution || "someone"} ${branding?.sms_customization?.off_net_third || "with note:"} "${transfer.note || ""}". ${branding?.sms_customization?.off_net_fourth || "Claim at:"} ${shortlink}`;
          setWaitingForShortlink(false);
        } else if (transfer.on_network && transfer.status === "SUCCEEDED") {
          // On-network transfer
          message = `${branding?.sms_customization?.on_net_first || "You've received"} $${transfer.amount?.value} ${branding?.sms_customization?.on_net_second || "with note:"} "${transfer.note || ""}". ${branding?.sms_customization?.on_net_third || ""}`;
          setWaitingForShortlink(false);
        }
        if (message) {
          clickSendSms(message, transfer.recipient.value);
          setMessageSent(true);
        }
      }
    }
  }, [transferData, messageSent, waitingForShortlink, shortlinkTimeout]);

  if (loading) {
    return (
      <main className="center_page">
        <SpinnerDotted size={50} thickness={100} speed={100} color="#36ad47" />
      </main>
    );
  }

  if (error) {
    return (
      <main className="center_page">
        <div>
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      </main>
    );
  }

  // Handle waiting for shortlink generation
  if (waitingForShortlink) {
    return (
      <main className="center_page">
        <SpinnerDotted size={50} thickness={100} speed={100} color="#36ad47" />
        <div style={{ marginTop: "20px" }}>
          <h3>Generating Payment Link</h3>
          <p>Please wait while we generate your payment link...</p>
        </div>
      </main>
    );
  }

  // Handle shortlink timeout
  if (shortlinkTimeout) {
    return (
      <main className="center_page">
        <div>
          <h3>Something Went Wrong</h3>
          <p>Something went wrong generating the payment link. Please try again later.</p>
        </div>
      </main>
    );
  }

  // Handle FAILED status
  if (transferData?.status === "FAILED") {
    return (
      <main className="center_page">
        <div>
          <h3>Transfer Failed</h3>
          <p>The transfer has failed and the funds have been moved back into your account.</p>
        </div>
      </main>
    );
  }

  if (transferData?.recipient?.type === "email") {
    return (
      <main className="center_page">
        <div>
          <h3>Email Sent</h3>
          <p>Your email has been sent to the recipient</p>
        </div>
      </main>
    );
  }

  return (
    <main className="center_page">
      <div>
        <h3>Sending Text Message</h3>
        <p>Opening your messaging app...</p>
      </div>
    </main>
  );
};

export default SendTextPage;